// The route tree, without a router around it.
//
// main.tsx wraps this in <BrowserRouter> for the browser; entry-prerender.tsx
// wraps it in <StaticRouter> to write each public page to HTML at build time.
// Keeping the tree in one place is what guarantees the prerendered markup and
// the live app are the same components.
//
// Every page is its own chunk. The signed-in routes use lazyRoute() and show a
// loader while they download. The public pages use publicPage(), which can be
// loaded BEFORE the first render: main.tsx awaits preloadRouteFor() for the
// current URL, and the prerender awaits every public page, so neither ever
// renders a loader in place of content. See src/lib/lazy-route.ts.
//
// Adding a <Route>? It must also be listed in src/lib/seo.ts — PUBLIC_PAGES if
// it should be indexed, APP_SHELL_ROUTES if it is app-only. A test enforces it.

import { Suspense, isValidElement, useEffect, useRef } from "react";
import {
  Route,
  Routes,
  createRoutesFromElements,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { trackPageView } from "@/lib/meta-pixel";
import { isAppShellPath } from "@/lib/seo";
import { SeoHead } from "@/lib/use-seo";
import { AuthProvider } from "@/lib/auth";
import { WorkspaceProvider } from "@/lib/workspace";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { MockingbirdLoader } from "@/components/ui/mockingbird-loader";
import { lazyRoute, publicPage } from "@/lib/lazy-route";
const Landing = publicPage(() => import("@/routes/landing").then((m) => m.Landing));
const Pricing = publicPage(() => import("@/routes/pricing").then((m) => m.Pricing));
const HowItWorks = publicPage(() => import("@/routes/how-it-works").then((m) => m.HowItWorks));
const Security = publicPage(() => import("@/routes/security").then((m) => m.Security));
const NexlyticsCaseStudy = publicPage(() => import("@/routes/case-studies/nexlytics").then((m) => m.NexlyticsCaseStudy));
const CompetitorAdResearch = publicPage(() => import("@/routes/competitor-ad-research").then((m) => m.CompetitorAdResearch));
const AiAdGenerator = publicPage(() => import("@/routes/ai-ad-generator").then((m) => m.AiAdGenerator));
const MetaAdLibraryAlternative = publicPage(() => import("@/routes/meta-ad-library-alternative").then((m) => m.MetaAdLibraryAlternative));
const Examples = publicPage(() => import("@/routes/examples").then((m) => m.Examples));
const AlternativePage = publicPage(() => import("@/components/marketing/alternative").then((m) => m.AlternativePage));
const Blog = publicPage(() => import("@/routes/blog").then((m) => m.Blog));
const AdvantagePlusCatalogueAdsPost = publicPage(() => import("@/routes/blog/advantage-plus-catalogue-ads").then((m) => m.AdvantagePlusCatalogueAdsPost));
const DownloadAdLibraryVideosPost = publicPage(() => import("@/routes/blog/download-meta-ad-library-videos").then((m) => m.DownloadAdLibraryVideosPost));
const AdLibraryProductResearchPost = publicPage(() => import("@/routes/blog/facebook-ad-library-product-research").then((m) => m.AdLibraryProductResearchPost));
const FacebookAdTextLimitsPost = publicPage(() => import("@/routes/blog/facebook-ad-text-limits").then((m) => m.FacebookAdTextLimitsPost));
const FacebookAdsTargetingPost = publicPage(() => import("@/routes/blog/facebook-ads-targeting-options").then((m) => m.FacebookAdsTargetingPost));
const InstallMetaPixelPost = publicPage(() => import("@/routes/blog/install-meta-pixel-shopify-wordpress").then((m) => m.InstallMetaPixelPost));
const AdLibraryFiltersPost = publicPage(() => import("@/routes/blog/meta-ad-library-filters-explained").then((m) => m.AdLibraryFiltersPost));
const MetaAdsCertificationPost = publicPage(() => import("@/routes/blog/meta-ads-certification-blueprint").then((m) => m.MetaAdsCertificationPost));
const MetaAdsNonprofitsPost = publicPage(() => import("@/routes/blog/meta-ads-for-nonprofits").then((m) => m.MetaAdsNonprofitsPost));
const MetaLeadAdsPost = publicPage(() => import("@/routes/blog/meta-lead-ads-instant-forms").then((m) => m.MetaLeadAdsPost));
const AdvantagePlusAudiencePost = publicPage(() => import("@/routes/blog/advantage-plus-audience-explained").then((m) => m.AdvantagePlusAudiencePost));
const AdvantagePlusPlacementsPost = publicPage(() => import("@/routes/blog/advantage-plus-placements-vs-manual").then((m) => m.AdvantagePlusPlacementsPost));
const CreateMetaAdAccountPost = publicPage(() => import("@/routes/blog/create-meta-ad-account").then((m) => m.CreateMetaAdAccountPost));
const FacebookAdsRealEstatePost = publicPage(() => import("@/routes/blog/facebook-ads-for-real-estate").then((m) => m.FacebookAdsRealEstatePost));
const HowToRunInstagramAdsPost = publicPage(() => import("@/routes/blog/how-to-run-instagram-ads").then((m) => m.HowToRunInstagramAdsPost));
const MetaAdsBidStrategiesPost = publicPage(() => import("@/routes/blog/meta-ads-bid-strategies").then((m) => m.MetaAdsBidStrategiesPost));
const MetaAdsBillingPost = publicPage(() => import("@/routes/blog/meta-ads-billing-and-payments").then((m) => m.MetaAdsBillingPost));
const MetaAdsMetricsPost = publicPage(() => import("@/routes/blog/meta-ads-metrics-explained").then((m) => m.MetaAdsMetricsPost));
const ReelsAdsPost = publicPage(() => import("@/routes/blog/reels-ads-facebook-instagram").then((m) => m.ReelsAdsPost));
const WhatIsMetaBusinessSuitePost = publicPage(() => import("@/routes/blog/what-is-meta-business-suite").then((m) => m.WhatIsMetaBusinessSuitePost));
const FacebookAdCreativeExplainedPost = publicPage(() => import("@/routes/blog/facebook-ad-creative-explained").then((m) => m.FacebookAdCreativeExplainedPost));
const FacebookAdsVsBoostedPostsPost = publicPage(() => import("@/routes/blog/facebook-ads-vs-boosted-posts").then((m) => m.FacebookAdsVsBoostedPostsPost));
const FacebookAdsWrongLanguagePost = publicPage(() => import("@/routes/blog/facebook-ads-wrong-language-or-location").then((m) => m.FacebookAdsWrongLanguagePost));
const HowFacebookAdCampaignsWorkPost = publicPage(() => import("@/routes/blog/how-facebook-ad-campaigns-work").then((m) => m.HowFacebookAdCampaignsWorkPost));
const HowMuchDoFacebookAdsCostPost = publicPage(() => import("@/routes/blog/how-much-do-facebook-ads-cost").then((m) => m.HowMuchDoFacebookAdsCostPost));
const IsMetaAdsManagerDownPost = publicPage(() => import("@/routes/blog/is-meta-ads-manager-down").then((m) => m.IsMetaAdsManagerDownPost));
const MetaAdCampaignNotSpendingPost = publicPage(() => import("@/routes/blog/meta-ad-campaign-not-spending").then((m) => m.MetaAdCampaignNotSpendingPost));
const MetaAdsCampaignObjectivesPost = publicPage(() => import("@/routes/blog/meta-ads-campaign-objectives").then((m) => m.MetaAdsCampaignObjectivesPost));
const MetaPixelExplainedPost = publicPage(() => import("@/routes/blog/meta-pixel-explained").then((m) => m.MetaPixelExplainedPost));
const PauseStopDeleteCampaignPost = publicPage(() => import("@/routes/blog/pause-stop-delete-meta-ad-campaign").then((m) => m.PauseStopDeleteCampaignPost));
const AdvantagePlusCampaigns2026Post = publicPage(() => import("@/routes/blog/advantage-plus-campaigns-2026").then((m) => m.AdvantagePlusCampaigns2026Post));
const CopyingVsInspirationPost = publicPage(() => import("@/routes/blog/copying-vs-inspiration-competitor-ads").then((m) => m.CopyingVsInspirationPost));
const HowLongAdRunningPost = publicPage(() => import("@/routes/blog/how-long-has-a-facebook-ad-been-running").then((m) => m.HowLongAdRunningPost));
const MetaAdLibraryApiPost = publicPage(() => import("@/routes/blog/meta-ad-library-api").then((m) => m.MetaAdLibraryApiPost));
const MetaAdsAiConnectorsPost = publicPage(() => import("@/routes/blog/meta-ads-ai-connectors-mcp").then((m) => m.MetaAdsAiConnectorsPost));
const MetaAdsAttributionSettingsPost = publicPage(() => import("@/routes/blog/meta-ads-attribution-settings").then((m) => m.MetaAdsAttributionSettingsPost));
const MetaAdsLearningPhasePost = publicPage(() => import("@/routes/blog/meta-ads-learning-phase").then((m) => m.MetaAdsLearningPhasePost));
const MetaAdsManagerVsBusinessSuitePost = publicPage(() => import("@/routes/blog/meta-ads-manager-vs-business-suite").then((m) => m.MetaAdsManagerVsBusinessSuitePost));
const MetaQ22026ResultsPost = publicPage(() => import("@/routes/blog/meta-q2-2026-results-for-advertisers").then((m) => m.MetaQ22026ResultsPost));
const ThreadsAds2026Post = publicPage(() => import("@/routes/blog/threads-ads-2026").then((m) => m.ThreadsAds2026Post));
const AdvantagePlusCreativeEnhancementsPost = publicPage(() => import("@/routes/blog/advantage-plus-creative-enhancements").then((m) => m.AdvantagePlusCreativeEnhancementsPost));
const MetaAdLibrarySpendPost = publicPage(() => import("@/routes/blog/does-meta-ad-library-show-spend").then((m) => m.MetaAdLibrarySpendPost));
const MetaLessPersonalisedAdsEuPost = publicPage(() => import("@/routes/blog/meta-less-personalised-ads-eu").then((m) => m.MetaLessPersonalisedAdsEuPost));
const MetaEuPoliticalAdsPost = publicPage(() => import("@/routes/blog/meta-eu-political-ads-one-year-on").then((m) => m.MetaEuPoliticalAdsPost));
const MetaFullyAutomatedAdsPost = publicPage(() => import("@/routes/blog/meta-fully-automated-ads-2026").then((m) => m.MetaFullyAutomatedAdsPost));
const MetaScamAdsPost = publicPage(() => import("@/routes/blog/meta-scam-ads-what-advertisers-should-know").then((m) => m.MetaScamAdsPost));
const OtherAdLibrariesPost = publicPage(() => import("@/routes/blog/google-linkedin-tiktok-ad-libraries").then((m) => m.OtherAdLibrariesPost));
const MetaAdsChangesOctober2026Post = publicPage(() => import("@/routes/blog/meta-ads-changes-october-2026").then((m) => m.MetaAdsChangesOctober2026Post));
const MetaAndromedaGemPost = publicPage(() => import("@/routes/blog/meta-andromeda-gem-explained").then((m) => m.MetaAndromedaGemPost));
const MetaAdvertisingWeek2026Post = publicPage(() => import("@/routes/blog/meta-advertising-week-2026").then((m) => m.MetaAdvertisingWeek2026Post));
const Guides = publicPage(() => import("@/routes/guides").then((m) => m.Guides));
const FindCompetitorAdsInstagramGuide = publicPage(() => import("@/routes/guides/find-competitor-ads-instagram").then((m) => m.FindCompetitorAdsInstagramGuide));
const SpyOnCompetitorAdsGuide = publicPage(() => import("@/routes/guides/spy-on-competitor-ads").then((m) => m.SpyOnCompetitorAdsGuide));
const MetaAdSizesGuide = publicPage(() => import("@/routes/guides/meta-ad-sizes").then((m) => m.MetaAdSizesGuide));
const TestAdCreativeGuide = publicPage(() => import("@/routes/guides/test-ad-creative").then((m) => m.TestAdCreativeGuide));
const MetaAdLibraryGuide = publicPage(() => import("@/routes/guides/meta-ad-library").then((m) => m.MetaAdLibraryGuide));
const PublishMetaAdsGuide = publicPage(() => import("@/routes/guides/publish-meta-ads").then((m) => m.PublishMetaAdsGuide));
const MetaAdRankingGuide = publicPage(() => import("@/routes/guides/meta-ad-ranking").then((m) => m.MetaAdRankingGuide));
const Lifetime = publicPage(() => import("@/routes/lifetime").then((m) => m.Lifetime));
const Terms = publicPage(() => import("@/routes/legal/terms").then((m) => m.Terms));
const Privacy = publicPage(() => import("@/routes/legal/privacy").then((m) => m.Privacy));
const NotFound = publicPage(() => import("@/routes/not-found").then((m) => m.NotFound));

// Everything below is its own chunk, fetched on first visit. These are the
// routes a search engine never sees: signed-in pages and one-off flows. The
// imports above stay static because those pages are prerendered, and a lazy
// component prerenders as its Suspense fallback (see lib/lazy-route.ts).
const AuthCallback = lazyRoute(() => import("@/components/auth/auth-callback").then((m) => m.AuthCallback));
const AcceptInvitation = lazyRoute(() => import("@/components/invitations/accept-invitation").then((m) => m.AcceptInvitation));
const AppShell = lazyRoute(() => import("@/components/layout/app-shell").then((m) => m.AppShell));
const LifetimeThankYou = lazyRoute(() => import("@/routes/lifetime/thank-you").then((m) => m.LifetimeThankYou));
const LifetimeUpgrade = lazyRoute(() => import("@/routes/lifetime/upgrade").then((m) => m.LifetimeUpgrade));
const Login = lazyRoute(() => import("@/routes/login").then((m) => m.Login));
const Signup = lazyRoute(() => import("@/routes/signup").then((m) => m.Signup));
const ResetPassword = lazyRoute(() => import("@/routes/reset-password").then((m) => m.ResetPassword));
const Account = lazyRoute(() => import("@/routes/account").then((m) => m.Account));
const Dashboard = lazyRoute(() => import("@/routes/dashboard").then((m) => m.Dashboard));
const CreateAd = lazyRoute(() => import("@/routes/create-ad").then((m) => m.CreateAd));
const MetaAnalytics = lazyRoute(() => import("@/routes/meta-analytics").then((m) => m.MetaAnalytics));
const Onboarding = lazyRoute(() => import("@/routes/onboarding").then((m) => m.Onboarding));
const Brand = lazyRoute(() => import("@/routes/brand").then((m) => m.Brand));
const Connections = lazyRoute(() => import("@/routes/settings/connections").then((m) => m.Connections));
const Billing = lazyRoute(() => import("@/routes/settings/billing").then((m) => m.Billing));
const Usage = lazyRoute(() => import("@/routes/settings/usage").then((m) => m.Usage));
const Members = lazyRoute(() => import("@/routes/settings/members").then((m) => m.Members));
const AuditLog = lazyRoute(() => import("@/routes/settings/audit").then((m) => m.AuditLog));
const NewRun = lazyRoute(() => import("@/routes/runs/new").then((m) => m.NewRun));
const RunDetail = lazyRoute(() => import("@/routes/runs/detail").then((m) => m.RunDetail));
const CmoPage = lazyRoute(() => import("@/routes/cmo").then((m) => m.CmoPage));

/**
 * Fires a Meta Pixel PageView on every SPA route change after the first
 * load. initMetaPixel() in main.tsx already sends the initial PageView, so this
 * skips its own first run to avoid double-counting the landing page.
 */
function MetaPixelPageViews() {
  const location = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      // initMetaPixel() already sent the PageView for the initial load.
      isFirst.current = false;
      return;
    }
    trackPageView();
    // Only the pathname (not search/hash) is what Meta's PageView cares
    // about distinguishing between SPA routes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return null;
}

/**
 * Shown while a lazy route's chunk downloads. Same card ProtectedRoute shows
 * while the session resolves, so the two waits read as one.
 */
function RouteFallback() {
  return (
    <main className="grid min-h-full place-items-center px-6">
      <MockingbirdLoader screen label="Mockingbird is loading..." />
    </main>
  );
}

/**
 * A public page opens at its top. The router on its own keeps the scroll
 * position of the page the visitor came from, so a footer link used to land
 * them at the bottom of the next page.
 *
 * Left alone: back and forward (the browser restores where they were), a
 * #hash link (its own handler scrolls), and the signed-in app.
 */
function ScrollToTopOnNavigate() {
  const { pathname, hash } = useLocation();
  const navigation = useNavigationType();
  useEffect(() => {
    if (navigation === "POP" || hash || isAppShellPath(pathname)) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, navigation]);
  return null;
}

/**
 * The route table. A module-level constant, not JSX inside AppRoutes, so the
 * same elements can be matched against a URL before anything renders.
 */
const ROUTE_TABLE = (
  <>
    <Route path="/" element={<Landing />} />
    <Route path="/pricing" element={<Pricing />} />
    <Route path="/how-it-works" element={<HowItWorks />} />
    <Route path="/security" element={<Security />} />
    <Route path="/case-studies/nexlytics" element={<NexlyticsCaseStudy />} />
    <Route path="/competitor-ad-research" element={<CompetitorAdResearch />} />
    <Route path="/ai-ad-generator" element={<AiAdGenerator />} />
    <Route path="/meta-ad-library-alternative" element={<MetaAdLibraryAlternative />} />
    <Route path="/examples" element={<Examples />} />
    <Route path="/foreplay-alternative" element={<AlternativePage path="/foreplay-alternative" />} />
    <Route path="/adspy-alternative" element={<AlternativePage path="/adspy-alternative" />} />
    <Route path="/adcreative-ai-alternative" element={<AlternativePage path="/adcreative-ai-alternative" />} />
    <Route path="/gethookd-alternative" element={<AlternativePage path="/gethookd-alternative" />} />
    <Route path="/adstellar-alternative" element={<AlternativePage path="/adstellar-alternative" />} />
    <Route path="/atria-alternative" element={<AlternativePage path="/atria-alternative" />} />
    <Route path="/guides" element={<Guides />} />
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/advantage-plus-catalogue-ads" element={<AdvantagePlusCatalogueAdsPost />} />
    <Route path="/blog/download-meta-ad-library-videos" element={<DownloadAdLibraryVideosPost />} />
    <Route path="/blog/facebook-ad-library-product-research" element={<AdLibraryProductResearchPost />} />
    <Route path="/blog/facebook-ad-text-limits" element={<FacebookAdTextLimitsPost />} />
    <Route path="/blog/facebook-ads-targeting-options" element={<FacebookAdsTargetingPost />} />
    <Route path="/blog/install-meta-pixel-shopify-wordpress" element={<InstallMetaPixelPost />} />
    <Route path="/blog/meta-ad-library-filters-explained" element={<AdLibraryFiltersPost />} />
    <Route path="/blog/meta-ads-certification-blueprint" element={<MetaAdsCertificationPost />} />
    <Route path="/blog/meta-ads-for-nonprofits" element={<MetaAdsNonprofitsPost />} />
    <Route path="/blog/meta-lead-ads-instant-forms" element={<MetaLeadAdsPost />} />
    <Route path="/blog/advantage-plus-audience-explained" element={<AdvantagePlusAudiencePost />} />
    <Route path="/blog/advantage-plus-placements-vs-manual" element={<AdvantagePlusPlacementsPost />} />
    <Route path="/blog/create-meta-ad-account" element={<CreateMetaAdAccountPost />} />
    <Route path="/blog/facebook-ads-for-real-estate" element={<FacebookAdsRealEstatePost />} />
    <Route path="/blog/how-to-run-instagram-ads" element={<HowToRunInstagramAdsPost />} />
    <Route path="/blog/meta-ads-bid-strategies" element={<MetaAdsBidStrategiesPost />} />
    <Route path="/blog/meta-ads-billing-and-payments" element={<MetaAdsBillingPost />} />
    <Route path="/blog/meta-ads-metrics-explained" element={<MetaAdsMetricsPost />} />
    <Route path="/blog/reels-ads-facebook-instagram" element={<ReelsAdsPost />} />
    <Route path="/blog/what-is-meta-business-suite" element={<WhatIsMetaBusinessSuitePost />} />
    <Route path="/blog/facebook-ad-creative-explained" element={<FacebookAdCreativeExplainedPost />} />
    <Route path="/blog/facebook-ads-vs-boosted-posts" element={<FacebookAdsVsBoostedPostsPost />} />
    <Route path="/blog/facebook-ads-wrong-language-or-location" element={<FacebookAdsWrongLanguagePost />} />
    <Route path="/blog/how-facebook-ad-campaigns-work" element={<HowFacebookAdCampaignsWorkPost />} />
    <Route path="/blog/how-much-do-facebook-ads-cost" element={<HowMuchDoFacebookAdsCostPost />} />
    <Route path="/blog/is-meta-ads-manager-down" element={<IsMetaAdsManagerDownPost />} />
    <Route path="/blog/meta-ad-campaign-not-spending" element={<MetaAdCampaignNotSpendingPost />} />
    <Route path="/blog/meta-ads-campaign-objectives" element={<MetaAdsCampaignObjectivesPost />} />
    <Route path="/blog/meta-pixel-explained" element={<MetaPixelExplainedPost />} />
    <Route path="/blog/pause-stop-delete-meta-ad-campaign" element={<PauseStopDeleteCampaignPost />} />
    <Route path="/blog/advantage-plus-campaigns-2026" element={<AdvantagePlusCampaigns2026Post />} />
    <Route path="/blog/copying-vs-inspiration-competitor-ads" element={<CopyingVsInspirationPost />} />
    <Route path="/blog/how-long-has-a-facebook-ad-been-running" element={<HowLongAdRunningPost />} />
    <Route path="/blog/meta-ad-library-api" element={<MetaAdLibraryApiPost />} />
    <Route path="/blog/meta-ads-ai-connectors-mcp" element={<MetaAdsAiConnectorsPost />} />
    <Route path="/blog/meta-ads-attribution-settings" element={<MetaAdsAttributionSettingsPost />} />
    <Route path="/blog/meta-ads-learning-phase" element={<MetaAdsLearningPhasePost />} />
    <Route path="/blog/meta-ads-manager-vs-business-suite" element={<MetaAdsManagerVsBusinessSuitePost />} />
    <Route path="/blog/meta-q2-2026-results-for-advertisers" element={<MetaQ22026ResultsPost />} />
    <Route path="/blog/threads-ads-2026" element={<ThreadsAds2026Post />} />
    <Route path="/blog/advantage-plus-creative-enhancements" element={<AdvantagePlusCreativeEnhancementsPost />} />
    <Route path="/blog/does-meta-ad-library-show-spend" element={<MetaAdLibrarySpendPost />} />
    <Route path="/blog/meta-less-personalised-ads-eu" element={<MetaLessPersonalisedAdsEuPost />} />
    <Route path="/blog/meta-eu-political-ads-one-year-on" element={<MetaEuPoliticalAdsPost />} />
    <Route path="/blog/meta-fully-automated-ads-2026" element={<MetaFullyAutomatedAdsPost />} />
    <Route path="/blog/meta-scam-ads-what-advertisers-should-know" element={<MetaScamAdsPost />} />
    <Route path="/blog/google-linkedin-tiktok-ad-libraries" element={<OtherAdLibrariesPost />} />
    <Route path="/blog/meta-ads-changes-october-2026" element={<MetaAdsChangesOctober2026Post />} />
    <Route path="/blog/meta-andromeda-gem-explained" element={<MetaAndromedaGemPost />} />
    <Route path="/blog/meta-advertising-week-2026" element={<MetaAdvertisingWeek2026Post />} />
    <Route path="/guides/find-competitor-ads-instagram" element={<FindCompetitorAdsInstagramGuide />} />
    <Route path="/guides/spy-on-competitor-ads" element={<SpyOnCompetitorAdsGuide />} />
    <Route path="/guides/meta-ad-sizes" element={<MetaAdSizesGuide />} />
    <Route path="/guides/test-ad-creative" element={<TestAdCreativeGuide />} />
    <Route path="/guides/meta-ad-library" element={<MetaAdLibraryGuide />} />
    <Route path="/guides/how-to-publish-meta-ads" element={<PublishMetaAdsGuide />} />
    <Route path="/guides/meta-ad-ranking" element={<MetaAdRankingGuide />} />
    <Route path="/lifetime" element={<Lifetime />} />
    <Route path="/lifetime/thank-you" element={<LifetimeThankYou />} />
    <Route
      path="/lifetime/upgrade"
      element={
        <ProtectedRoute>
          <LifetimeUpgrade />
        </ProtectedRoute>
      }
    />
    <Route path="/login" element={<Login />} />
    <Route path="/signup" element={<Signup />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/auth/callback" element={<AuthCallback />} />
    <Route path="/invitations/accept" element={<AcceptInvitation />} />
    <Route path="/legal/terms" element={<Terms />} />
    <Route path="/legal/privacy" element={<Privacy />} />
    <Route
      element={
        <ProtectedRoute>
          <AppShell />
        </ProtectedRoute>
      }
    >
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/create-ad" element={<CreateAd />} />
      <Route path="/meta-analytics" element={<MetaAnalytics />} />
      <Route path="/brand" element={<Brand />} />
      <Route path="/account" element={<Account />} />
      <Route path="/runs/new" element={<NewRun />} />
      <Route path="/runs/:id" element={<RunDetail />} />
      <Route path="/cmo" element={<CmoPage />} />
      <Route path="/settings/billing" element={<Billing />} />
      <Route path="/settings/usage" element={<Usage />} />
      <Route path="/settings/members" element={<Members />} />
      <Route path="/settings/team" element={<Members />} />
      <Route path="/settings/connections" element={<Connections />} />
      {/* Owner/admin only. The route itself is reachable by anyone
          signed in: the page renders an explicit no-access state
          rather than a redirect, so a viewer who follows a link
          never sees an empty table that reads as "nothing
          happened". */}
      <Route path="/settings/audit" element={<AuditLog />} />
    </Route>
    <Route path="*" element={<NotFound />} />
  </>
);

const ROUTE_OBJECTS = createRoutesFromElements(ROUTE_TABLE);

/**
 * Downloads the page component(s) the given URL will render, so the first
 * render can show them at once. Resolves immediately for routes that are not
 * publicPage() components (the signed-in app keeps its loader).
 */
export function preloadRouteFor(pathname: string): Promise<void> {
  const pending: Array<Promise<void>> = [];
  for (const match of matchRoutes(ROUTE_OBJECTS, pathname) ?? []) {
    const element = match.route.element;
    const type: unknown = isValidElement(element) ? element.type : null;
    if (typeof type === "function" && "preload" in type) {
      pending.push((type as { preload: () => Promise<void> }).preload());
    }
  }
  return Promise.all(pending).then(() => undefined);
}

/**
 * Fetches a page's chunk when a visitor shows intent to open it: the pointer
 * enters a link, a finger touches it, or it takes keyboard focus. By the time
 * the click lands the chunk is usually in memory, so in-site navigation is as
 * quick as it was when every page shipped in one bundle.
 */
function PreloadOnIntent() {
  useEffect(() => {
    const onIntent = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest("a[href]");
      if (!(link instanceof HTMLAnchorElement) || link.origin !== window.location.origin) return;
      // A failed preload is not an error here: the click will try again.
      preloadRouteFor(link.pathname).catch(() => {});
    };
    const options = { capture: true, passive: true } as const;
    document.addEventListener("pointerover", onIntent, options);
    document.addEventListener("touchstart", onIntent, options);
    document.addEventListener("focusin", onIntent, options);
    return () => {
      document.removeEventListener("pointerover", onIntent, options);
      document.removeEventListener("touchstart", onIntent, options);
      document.removeEventListener("focusin", onIntent, options);
    };
  }, []);
  return null;
}

export function AppRoutes() {
  return (
    <AuthProvider>
      <WorkspaceProvider>
        <MetaPixelPageViews />
        <ScrollToTopOnNavigate />
        <PreloadOnIntent />
        <SeoHead />
        <Suspense fallback={<RouteFallback />}>
          <Routes>{ROUTE_TABLE}</Routes>
        </Suspense>
      </WorkspaceProvider>
    </AuthProvider>
  );
}
